import { useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';

function formatWarehouseLocation(row) {
  const warehouse = row?.warehouse?.name || row?.warehouse?.code || '';
  const location = row?.warehouse?.location?.name
    || row?.warehouse?.location?.code
    || '';
  return [warehouse, location].filter(Boolean).join(' / ') || '—';
}

function formatMaterial(row) {
  const name = row?.mtr?.name || '';
  const no = row?.mtr?.mtrNo || '';
  if (name && no) {
    return `${no} ${name}`;
  }
  return name || no || '—';
}

const StockScanAmbiguousDialog = ({
  open,
  onOpenChange,
  candidates = [],
  onConfirm,
  onCancel,
}) => {
  /** null＝本次開啟尚未選；關閉後由 hook session key remount 重置。 */
  const [selectedIdDraft, setSelectedIdDraft] = useState(null);
  const selectedId = open ? selectedIdDraft : null;

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      setSelectedIdDraft(null);
    }
    onOpenChange(nextOpen);
  }

  function handleCancel() {
    setSelectedIdDraft(null);
    onCancel?.();
    onOpenChange(false);
  }

  function handleConfirm() {
    const row = candidates.find((item) => item.id === selectedId);
    if (!row) {
      return;
    }
    onConfirm?.(row);
    setSelectedIdDraft(null);
    onOpenChange(false);
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="選擇庫存"
      description="此庫存編號對應多筆庫存，請單選一筆加入清單。"
      headerVariant="primary"
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      confirmDisabled={!selectedId}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText="加入"
    >
      <ul className="m-0 list-none space-y-2 p-0">
        {candidates.map((row) => {
          const inputId = `stock-scan-ambiguous-${row.id}`;
          const checked = selectedId === row.id;

          return (
            <li key={row.id}>
              <label
                htmlFor={inputId}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-md border px-3 py-3',
                  'border-slate-200 transition-colors',
                  checked && 'border-primary-400 bg-primary-50 ring-2 ring-primary-200',
                  !checked && 'hover:bg-slate-50',
                )}
              >
                <input
                  id={inputId}
                  type="radio"
                  name="stock-scan-ambiguous"
                  className="mt-1 size-4 shrink-0 accent-primary"
                  checked={checked}
                  onChange={() => setSelectedIdDraft(row.id)}
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="font-mono text-sm font-medium text-slate-800">
                    {row.stockNo || '—'}
                  </p>
                  <p className="truncate text-sm text-slate-700">
                    {formatMaterial(row)}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>
                      倉／儲
                      {' '}
                      {formatWarehouseLocation(row)}
                    </span>
                    <span className="tabular-nums">
                      系統量
                      {' '}
                      {row.systemQty ?? row.baseQty ?? '—'}
                    </span>
                  </div>
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </AppDialog>
  );
};

StockScanAmbiguousDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  candidates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    stockNo: PropTypes.string,
    systemQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    baseQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mtr: PropTypes.object,
    warehouse: PropTypes.object,
  })),
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

export default StockScanAmbiguousDialog;
